import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { SESSION_REFRESH_INTERVAL } from "@/lib/constants";
import { setUser as setSentryUser } from "@/lib/sentry";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  // Refresh session manually
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("Session refresh error:", error);
        setError(error);
        return;
      }

      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
        setError(null);
      }
    } catch (err) {
      console.error("Unexpected error during session refresh:", err);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Track user in Sentry for error monitoring
        if (session?.user) {
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
          });
        } else {
          setSentryUser(null);
        }

        // Handle specific auth events
        switch (event) {
          case "SIGNED_IN":
            setError(null);
            break;
          case "SIGNED_OUT":
            setSession(null);
            setUser(null);
            setSentryUser(null);
            setError(null);
            break;
          case "TOKEN_REFRESHED":
            setError(null);
            break;
          case "USER_UPDATED":
            break;
          case "PASSWORD_RECOVERY":
            break;
        }
      }
    );

    // Check for existing session on mount
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session initialization error:", error);
          setError(error);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setError(null);

        // Track user in Sentry for error monitoring
        if (session?.user) {
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
          });
        }
      } catch (err) {
        console.error("Unexpected error during auth initialization:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  // Set up periodic session refresh (tokens expire after 60 minutes)
  useEffect(() => {
    if (!session) return;

    const refreshInterval = setInterval(() => {
      refreshSession();
    }, SESSION_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [session, refreshSession]);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        // Only show error if it's not a "session not found" error
        if (!error.message.includes("session_not_found")) {
          console.error("Sign out error:", error);
          toast({
            title: "Sign out error",
            description: error.message,
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Unexpected sign out error:', error);
    } finally {
      // Always clear state to ensure UI updates
      setSession(null);
      setUser(null);
      setError(null);

      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, error, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
